import { z } from 'zod';
import { SLUG_MAX_LENGTH, SLUG_PATTERN } from '../slug.js';
/**
 * Taxonomy request schemas — phases.md Phase 5 task list, §2 ("every request body passes
 * through a Zod schema from `packages/shared`").
 *
 * Taxonomy rows follow the §1.15 bilingual rule: **one row, one slug, paired name
 * columns**, because a category is the same entity in both languages. Articles are the
 * opposite case and live in `content.ts`.
 */
export const slugSchema = z
    .string()
    .trim()
    .toLowerCase()
    .max(SLUG_MAX_LENGTH)
    .regex(SLUG_PATTERN, 'Slug must be lowercase letters, digits and single hyphens');
/** `#rgb`, `#rrggbb` or `#rrggbbaa` — the widths the `varchar(9)` column can hold. */
const hexColorSchema = z
    .string()
    .trim()
    .regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/, 'Expected a hex colour like #0f9d58');
const seoFields = {
    seoTitleAs: z.string().trim().max(200).nullish(),
    seoTitleEn: z.string().trim().max(200).nullish(),
    seoDescriptionAs: z.string().trim().max(400).nullish(),
    seoDescriptionEn: z.string().trim().max(400).nullish(),
};
export const categoryCreateSchema = z.object({
    /** Optional: derived from `nameEn` when omitted, then uniquified server-side. */
    slug: slugSchema.optional(),
    nameAs: z.string().trim().min(1).max(120),
    nameEn: z.string().trim().min(1).max(120),
    descriptionAs: z.string().trim().max(400).nullish(),
    descriptionEn: z.string().trim().max(400).nullish(),
    parentId: z.number().int().positive().nullish(),
    icon: z.string().trim().max(60).nullish(),
    color: hexColorSchema.nullish(),
    sortOrder: z.number().int().optional(),
    isActive: z.boolean().optional(),
    ...seoFields,
});
export const categoryUpdateSchema = categoryCreateSchema.partial();
/**
 * Reorder is its own endpoint rather than N `PATCH`es: the CMS drags a whole list at once,
 * and applying the moves one at a time would leave the order briefly wrong for readers.
 */
export const reorderSchema = z.object({
    order: z.array(z.number().int().positive()).min(1).max(200),
});
/**
 * Deleting a category with articles is refused; this is the reassignment the refusal
 * offers (Phase 5 task list).
 */
export const categoryDeleteSchema = z.object({
    reassignToId: z.number().int().positive().optional(),
});
export const districtCreateSchema = z.object({
    slug: slugSchema.optional(),
    nameAs: z.string().trim().min(1).max(120),
    nameEn: z.string().trim().min(1).max(120),
    descriptionAs: z.string().trim().max(400).nullish(),
    descriptionEn: z.string().trim().max(400).nullish(),
    sortOrder: z.number().int().optional(),
    isActive: z.boolean().optional(),
});
export const districtUpdateSchema = districtCreateSchema.partial();
export const tagCreateSchema = z.object({
    slug: slugSchema.optional(),
    nameAs: z.string().trim().min(1).max(120),
    nameEn: z.string().trim().min(1).max(120),
});
export const tagUpdateSchema = tagCreateSchema.partial();
export const authorCreateSchema = z.object({
    slug: slugSchema.optional(),
    nameAs: z.string().trim().min(1).max(160),
    nameEn: z.string().trim().min(1).max(160),
    bioAs: z.string().trim().max(2000).nullish(),
    bioEn: z.string().trim().max(2000).nullish(),
    photoMediaId: z.number().int().positive().nullish(),
    email: z.string().trim().toLowerCase().email().max(255).nullish(),
    /** The login account that writes under this byline, if any — see `authors` in content.ts. */
    userId: z.number().int().positive().nullish(),
    twitterHandle: z.string().trim().max(80).nullish(),
    facebookUrl: z.string().trim().url().max(255).nullish(),
    instagramUrl: z.string().trim().url().max(255).nullish(),
    isActive: z.boolean().optional(),
});
export const authorUpdateSchema = authorCreateSchema.partial();
/** §2: offset-based pagination for admin tables, default 20, max 100. */
export const adminListQuerySchema = z.object({
    search: z.string().trim().min(1).max(200).optional(),
    page: z.coerce.number().int().positive().default(1),
    perPage: z.coerce.number().int().positive().max(100).default(20),
});
//# sourceMappingURL=taxonomy.js.map